import type { AffectedMatch, FormErrors } from "@repo/shared-types";
import { clearToken, getToken } from "./auth";
import { data as dataRouter } from "react-router";

/** A failed request's answer: the API message and the rest of its body. */
export type ApiError = {
  message: string;
  status: number;
  errors?: FormErrors;
  /** A 409 asking to confirm: the played matches the action would change. */
  affectedMatches?: AffectedMatch[];
};

/**
 * The API error a request threw, or null for anything else. Requests throw
 * react-router `data()` values: the message in `data`, the body in `init`.
 */
export const apiError = (error: unknown): ApiError | null => {
  const { data, init } = (error ?? {}) as {
    data?: unknown;
    init?: Omit<ApiError, "message"> | null;
  };
  if (typeof init?.status !== "number") return null;
  return { ...init, message: typeof data === "string" ? data : "" };
};

/** The API refused the club's management or its POS: the club has no subscription in force. */
export const isSubscriptionRequired = (error: unknown) => apiError(error)?.status === 402;

type Method = RequestInit["method"];

const getInitDefault = (method: Method, body?: any) => {
  const init: RequestInit = {
    method,
    headers: {
      Accept: "application/json",
    },
  };
  const token = getToken();
  if (token && init?.headers) {
    init.headers = {
      ...init.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  if (!(body instanceof FormData)) {
    init.headers = {
      ...init.headers,
      "Content-Type": "application/json",
    };
  }
  return init;
};

const getUrl = (url: string | URL) => {
  if (url instanceof URL) {
    url.hostname = import.meta.env.VITE_API_URL;
    return url;
  }
  while (url.startsWith("/")) {
    url = url.slice(1);
  }
  return `${import.meta.env.VITE_API_URL}/${url}`;
};

const parseResponse = async <T = any>(res: Response) => {
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      clearToken();
      window.location.href = "/login";
      throw dataRouter("Unauthorized", { status: res.status });
    }
    if (res.status >= 399) {
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const dataRes = await res.json();
        throw dataRouter(dataRes.message, { status: res.status, ...dataRes });
      }
      const text = await res.text();
      throw dataRouter(text || `Error ${res.status}`, { status: res.status });
    }
    throw new Error(`HTTP ${res.status}`);
  }
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await res.json()
    : ((await res.text()) as any);
  return data as T;
};

function createMethod<T>(method: Method) {
  return async (url: string | URL, body?: any): Promise<T> => {
    const init = getInitDefault(method, body);
    if (body) {
      if (body instanceof FormData) {
        init.body = body;
      } else {
        init.body = JSON.stringify(body);
      }
    }
    const res = await fetch(getUrl(url), init);
    return parseResponse<T>(res);
  };
}

export const api = {
  get<T = any>(url: string | URL): Promise<T> {
    return createMethod<T>("GET")(url);
  },
  delete<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("DELETE")(url, body);
  },
  post<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("POST")(url, body);
  },
  patch<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("PATCH")(url, body);
  },
  put<T = any>(url: string | URL, body?: any): Promise<T> {
    return createMethod<T>("PUT")(url, body);
  },
};

/** A refused request of a club's TV, by status: the TV decides what each means. */
export class ScreenDeviceRequestError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`HTTP ${status}`);
    this.status = status;
  }
}

/**
 * A request from a club's TV. It has no user session, so it sends no user
 * token, and a refused credential is the TV's to handle (back to its pairing
 * code) rather than a logout.
 */
export const screenDeviceRequest = async <T>(
  method: "GET" | "POST",
  url: string,
  { credential, body }: { credential?: string; body?: unknown } = {}
): Promise<T> => {
  const res = await fetch(getUrl(url), {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(credential && { Authorization: `Bearer ${credential}` }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ScreenDeviceRequestError(res.status);
  return res.json() as Promise<T>;
};
